import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, FileText, Link as LinkIcon, Upload, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const [sources, setSources] = useState([]);
  const [mode, setMode] = useState("url");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const load = async () => { const { data } = await api.get("/kb/sources"); setSources(data); };
  useEffect(() => { load(); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const addUrl = () => run(async () => { await api.post("/kb/sources/url", { url }); setUrl(""); }, "Page indexed");
  const addText = () => run(async () => { await api.post("/kb/sources/text", { title, text }); setTitle(""); setText(""); }, "Text indexed");
  const addPdf = (file) => file && run(async () => { const fd = new FormData(); fd.append("file", file); await api.post("/kb/sources/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } }); }, "PDF indexed");
  const remove = (id) => run(() => api.delete(`/kb/sources/${id}`), "Source removed");
  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/kb/query", { question: q }); setResult(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const icon = { url: LinkIcon, pdf: FileText, text: BookOpen };

  return (
    <section className="border border-border rounded-md bg-card" data-testid="kb-settings">
      <div className="p-5 border-b border-border">
        <div className="text-sm font-medium flex items-center gap-2"><BookOpen className="w-4 h-4 text-muted-foreground" />Knowledge base</div>
        <div className="text-xs text-muted-foreground">Upload PDFs, web pages or text. The chat concierge searches these (keyword match, Claude picks the relevant parts) to answer product questions accurately.</div>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex gap-1 text-xs">
          {[["url", "Web page"], ["pdf", "PDF"], ["text", "Text"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`kb-mode-${k}`} onClick={() => setMode(k)} className={`px-2.5 py-1 rounded border transition-colors duration-150 ${mode === k ? "bg-muted text-foreground border-border" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {mode === "url" && <div className="flex gap-2"><Input data-testid="kb-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yoursite.com/pricing" className="h-8 text-sm" /><Button size="sm" onClick={addUrl} disabled={busy || !url} data-testid="kb-add-url"><LinkIcon className="w-3.5 h-3.5 mr-1.5" />Index page</Button></div>}
        {mode === "pdf" && <div className="flex gap-2 items-center"><Input ref={fileRef} data-testid="kb-pdf" type="file" accept=".pdf,application/pdf" className="h-8 text-sm" onChange={(e) => addPdf(e.target.files?.[0])} /><Upload className="w-4 h-4 text-muted-foreground" /></div>}
        {mode === "text" && <div className="space-y-2"><Input data-testid="kb-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Pricing FAQ)" className="h-8 text-sm" /><Textarea data-testid="kb-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste product info, FAQs, policies..." className="text-sm" /><Button size="sm" onClick={addText} disabled={busy || !text.trim()} data-testid="kb-add-text">Index text</Button></div>}

        <div className="border border-border rounded-md divide-y divide-border" data-testid="kb-sources">
          {sources.map((s) => { const I = icon[s.type] || BookOpen; return (
            <div key={s.id} className="flex items-center gap-3 px-3 py-2" data-testid={`kb-source-${s.id}`}>
              <I className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1"><div className="text-sm truncate">{s.title}</div><div className="text-[10px] mono text-muted-foreground truncate">{s.type} · {s.chunks} chunk{s.chunks === 1 ? "" : "s"} · {(s.chars / 1000).toFixed(1)}k chars{s.url ? ` · ${s.url}` : ""}</div></div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(s.id)} data-testid={`kb-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>); })}
          {sources.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground" data-testid="kb-empty">Nothing indexed yet.</div>}
        </div>

        <div className="space-y-2">
          <div className="flex gap-2"><Input data-testid="kb-question" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Test: ask a question your visitors might ask..." className="h-8 text-sm" /><Button size="sm" variant="outline" onClick={ask} disabled={busy || !q.trim()} data-testid="kb-ask"><Search className="w-3.5 h-3.5 mr-1.5" />{busy ? "..." : "Ask"}</Button></div>
          {result && (
            <div className="border border-border rounded-md p-3 text-sm space-y-2 bg-muted/20" data-testid="kb-answer">
              <div>{result.answer || (result.chunks.length ? "No answer generated." : "No matching knowledge found.")}</div>
              {result.chunks.length > 0 && <div className="text-[10px] mono text-muted-foreground">Matched: {result.chunks.slice(0, 3).map((c) => c.source_title).join(", ")}</div>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
